/**
 * The browser copy of the server's "Create with AI" draft check (backend-microservices
 * src/shared/common/aiDraft.ts, E1.1 / E1.6). Keep the two the same.
 *
 * The gateway (2377) checks every draft before it reaches this app. This copy is used only on
 * the fallback path, when the gateway cannot reach the AI and the browser calls ChatBolt itself:
 * a draft that fails the campaign data-format check, or whose prizes add up to more than the
 * budget, is refused here exactly as the server would refuse it, and never shown.
 */
export const DRAFT_FORMAT = 'luqta.contest-draft.v1';

/** The draw types the client and admin create forms can hold. */
export const DRAFT_DRAW_TYPES = ['random_draw', 'first_to_complete', 'instant_result', 'top_points', 'no_draw'];

const str = (v: any) => (typeof v === 'string' ? v.trim() : '');
const listOf = (v: any): any[] => (Array.isArray(v) ? v : []);

/** A draft date as epoch ms: epoch ms, epoch seconds or a date string; NaN when unreadable. */
export function draftTime(v: any): number {
  if (v === null || v === undefined || v === '' || v === 0) return NaN;
  const n = Number(v);
  if (Number.isFinite(n) && n > 1e12) return n;
  if (Number.isFinite(n) && n > 1e9) return n * 1000;
  return typeof v === 'string' ? Date.parse(v) : NaN;
}

/**
 * ChatBolt's draft as an object: a JSON string (with or without a ```json fence, or a trailing
 * comma) is parsed, one nested `data` is unwrapped, and the alternative names the create forms
 * already accept (prizes, levels, a level's `type`, winners) are copied to the names checked
 * below. Anything unreadable is returned as it came, and the check then fails it.
 */
export function readDraft(raw: any): any {
  let d = raw;
  if (typeof d === 'string') {
    const cleaned = d.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').replace(/,\s*([}\]])/g, '$1');
    try {
      d = JSON.parse(cleaned);
    } catch {
      const m = cleaned.match(/\{[\s\S]*\}/);
      try { d = m ? JSON.parse(m[0]) : raw; } catch { return raw; }
    }
  }
  if (!d || typeof d !== 'object' || Array.isArray(d)) return d;
  if (d.data && typeof d.data === 'object' && !Array.isArray(d.data) && !d.name) d = d.data;
  const out: any = { ...d };
  if (!Array.isArray(out._contest_prize)) out._contest_prize = d.prizes ?? d.prize ?? out._contest_prize;
  if (!Array.isArray(out._levels_of_contest)) out._levels_of_contest = d.levels ?? out._levels_of_contest;
  if (Array.isArray(out._levels_of_contest)) {
    out._levels_of_contest = out._levels_of_contest.map((l: any) =>
      l && typeof l === 'object' && !l.level_type && l.type ? { ...l, level_type: l.type } : l,
    );
  }
  if (out.number_of_winners === undefined || out.number_of_winners === null) {
    out.number_of_winners = d.winners ?? d.winner_count ?? out.number_of_winners;
  }
  return out;
}

export interface DraftLimits {
  /** The total prize value the draft may not exceed; 0 or missing = no budget. */
  budget?: number | null;
}

export interface DraftCheck {
  ok: boolean;
  /** What is wrong, in words the user (and the retry prompt) can read. */
  errors: string[];
  overBudget: boolean;
  totalRewardValue: number;
}

/** The data-format check (E1.1) and the budget check (E1.6). Pure. */
export function checkContestDraft(data: any, limits: DraftLimits = {}): DraftCheck {
  const errors: string[] = [];
  const d = data && typeof data === 'object' && !Array.isArray(data) ? data : null;
  if (!d) return { ok: false, errors: ['The draft is not a campaign object.'], overBudget: false, totalRewardValue: 0 };

  // Text
  if (!str(d.name)) errors.push('The campaign name is missing.');
  if (!str(d.details)) errors.push('The campaign description is missing.');

  // Mechanic
  const draw = str(d.draw_type);
  if (!DRAFT_DRAW_TYPES.includes(draw)) {
    errors.push(`The draw type is missing or unknown (one of ${DRAFT_DRAW_TYPES.join(', ')}).`);
  }
  const levels = listOf(d._levels_of_contest);
  if (!levels.length) errors.push('The campaign has no levels.');
  levels.forEach((l, i) => {
    if (!str(l?.name)) errors.push(`Level ${i + 1} has no name.`);
    if (!str(l?.level_type)) errors.push(`Level ${i + 1} has no level type.`);
  });

  // Rules
  const start = draftTime(d.start_date);
  const end = draftTime(d.end_date);
  if (!Number.isFinite(start)) errors.push('The start date is missing or unreadable.');
  if (!Number.isFinite(end)) errors.push('The end date is missing or unreadable.');
  if (Number.isFinite(start) && Number.isFinite(end) && end <= start) errors.push('The end date is not after the start date.');
  const winners = Number(d.number_of_winners);
  const everyoneWins = d.all_completers === true || draw === 'no_draw';
  if (!everyoneWins && !(Number.isInteger(winners) && winners >= 1)) errors.push('The number of winners is missing.');

  // Reward structure
  const prizes = listOf(d._contest_prize);
  if (!prizes.length && draw !== 'no_draw') errors.push('The campaign has no prizes.');
  let total = 0;
  prizes.forEach((p, i) => {
    const v = Number(p?.value);
    if (!str(p?.name)) errors.push(`Prize ${i + 1} has no name.`);
    if (!(Number.isFinite(v) && v > 0)) errors.push(`Prize ${i + 1} has no value.`);
    else total += v;
  });

  const budget = Number(limits.budget);
  const overBudget = Number.isFinite(budget) && budget > 0 && total > budget;
  if (overBudget) errors.push(`The prizes add up to ${total}, above the budget of ${budget}.`);

  return { ok: errors.length === 0, errors, overBudget, totalRewardValue: total };
}

const fmtDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

const DRAW_SENTENCE: Record<string, (n: number) => string> = {
  random_draw: (n) => `${n} winner${n === 1 ? ' is' : 's are'} drawn at random from the players who complete every level.`,
  first_to_complete: (n) => `The first ${n === 1 ? 'player' : `${n} players`} to complete every level win${n === 1 ? 's' : ''}.`,
  instant_result: () => 'Players learn at once whether they have won when they complete the campaign.',
  top_points: (n) => `The ${n === 1 ? 'player' : `${n} players`} with the most points win${n === 1 ? 's' : ''}.`,
  no_draw: () => 'There is no draw: completing the levels earns the reward.',
};

/** The draft's rules as plain sentences, built only from its own checked fields. */
export function draftRules(d: any): string[] {
  const levels = listOf(d._levels_of_contest);
  const winners = Number(d.number_of_winners) || 1;
  const rules = [
    `Runs from ${fmtDay(draftTime(d.start_date))} to ${fmtDay(draftTime(d.end_date))}.`,
    `Players complete ${levels.length} level${levels.length === 1 ? '' : 's'}: ${levels.map((l) => str(l.name)).join(', ')}.`,
    d.all_completers === true
      ? 'Every player who completes every level wins.'
      : (DRAW_SENTENCE[str(d.draw_type)] ?? DRAW_SENTENCE['random_draw'])(winners),
  ];
  const announce = draftTime(d.prize_announce_date);
  if (Number.isFinite(announce)) rules.push(`Winners are announced on ${fmtDay(announce)}.`);
  return rules;
}

/** Unicode left-to-right isolate and pop directional isolate. */
const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);
/** A YYYY-MM-DD date inside Arabic text, isolated so it reads left to right. */
const arDay = (ms: number) => LRI + fmtDay(ms) + PDI;

const DRAW_SENTENCE_AR: Record<string, (n: number) => string> = {
  random_draw: (n) => `يُسحب الفائزون عشوائيًا من بين اللاعبين الذين يُكملون جميع المراحل (عدد الفائزين: ${n}).`,
  first_to_complete: (n) => `يفوز أول من يُكمل جميع المراحل (عدد الفائزين: ${n}).`,
  instant_result: () => 'يعرف اللاعب فور إكمال الحملة ما إذا كان قد فاز.',
  top_points: (n) => `يفوز أصحاب أعلى النقاط (عدد الفائزين: ${n}).`,
  no_draw: () => 'لا يوجد سحب: إكمال المراحل يمنح المكافأة.',
};

/** The same rules in Arabic (E1.2), from the same fields; a level's Arabic name when it has one. */
export function draftRulesAr(d: any): string[] {
  const levels = listOf(d._levels_of_contest);
  const winners = Number(d.number_of_winners) || 1;
  const rules = [
    `تُقام الحملة من ${arDay(draftTime(d.start_date))} إلى ${arDay(draftTime(d.end_date))}.`,
    `يُكمل اللاعبون المراحل التالية (${levels.length}): ${levels.map((l) => str(l.arabic_name) || str(l.name)).join('، ')}.`,
    d.all_completers === true
      ? 'يفوز كل لاعب يُكمل جميع المراحل.'
      : (DRAW_SENTENCE_AR[str(d.draw_type)] ?? DRAW_SENTENCE_AR['random_draw'])(winners),
  ];
  const announce = draftTime(d.prize_announce_date);
  if (Number.isFinite(announce)) rules.push(`يُعلن عن الفائزين في ${arDay(announce)}.`);
  return rules;
}

/** The passing draft with its goal, summary and check stamp added. */
export function stampDraft(d: any, check: DraftCheck, opts: { goal?: string; budget?: number | null; attempts: number }) {
  const prizes = listOf(d._contest_prize);
  const goal = str(opts.goal);
  return {
    ...d,
    ...(goal ? { goal } : {}),
    mechanic: {
      draw_type: str(d.draw_type),
      number_of_winners: Number(d.number_of_winners) || null,
      all_completers: d.all_completers === true,
      levels: listOf(d._levels_of_contest).map((l) => ({ name: str(l.name), level_type: str(l.level_type) })),
    },
    rules: draftRules(d),
    rules_ar: draftRulesAr(d),
    reward_structure: {
      currency: str(prizes[0]?.currency) || null,
      total_value: check.totalRewardValue,
      budget: Number(opts.budget) > 0 ? Number(opts.budget) : null,
      prizes: prizes.map((p) => ({ name: str(p.name), value: Number(p.value) })),
    },
    draft_check: { format: DRAFT_FORMAT, passed: true, attempts: opts.attempts },
  };
}
